import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseUtils';
import './EventRelations.css';

const EventRelations = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // Mock events data for development
      const mockEvents = [
        {
          id: '1',
          name: 'Denver Youth Football Tournament',
          start_date: '2023-07-15',
          end_date: '2023-07-17',
          location: 'Denver, CO',
          venue: 'Denver Sports Complex',
          status: 'upcoming',
          organizer: 'Denver Youth Sports Association',
          contact_name: 'Sarah Johnson',
          contact_email: 'sarah.johnson@example.com',
          contact_phone: '(303) 555-1234',
          teams_count: 12,
          photography_package: 'Premium Tournament Package',
          notes: 'Full coverage of all games with team and individual photos',
          created_at: '2023-05-15T08:30:00Z'
        },
        {
          id: '2',
          name: 'Colorado Soccer Cup',
          start_date: '2023-08-10',
          end_date: '2023-08-12',
          location: 'Commerce City, CO',
          venue: 'Dick\'s Sporting Goods Park',
          status: 'upcoming',
          organizer: 'Colorado Rapids Youth',
          contact_name: 'Michael Rodriguez',
          contact_email: 'mrodriguez@example.com',
          contact_phone: '(720) 555-5678',
          teams_count: 24,
          photography_package: 'Standard Tournament Package',
          notes: 'Coverage of semi-finals and finals, with optional team photos',
          created_at: '2023-04-20T10:15:00Z'
        },
        {
          id: '3',
          name: 'Boulder Basketball Classic',
          start_date: '2023-09-20',
          end_date: '2023-09-22',
          location: 'Boulder, CO',
          venue: 'Boulder High School',
          status: 'upcoming',
          organizer: 'Boulder Basketball Club',
          contact_name: 'Amanda Chen',
          contact_email: 'achen@bolderbasketball.org',
          contact_phone: '(720) 555-2233',
          teams_count: 16,
          photography_package: 'Premium Tournament Package',
          notes: 'Full coverage with quick turnaround for digital delivery',
          created_at: '2023-04-12T11:30:00Z'
        },
        {
          id: '4',
          name: 'Spring High School Championship',
          start_date: '2023-05-05',
          end_date: '2023-05-07',
          location: 'Highlands Ranch, CO',
          venue: 'Eagles High School',
          status: 'completed',
          organizer: 'Eagles High School Athletics',
          contact_name: 'David Thompson',
          contact_email: 'dthompson@eagleshigh.edu',
          contact_phone: '(303) 555-3344',
          teams_count: 8,
          photography_package: 'Premium Championship Package',
          notes: 'Full coverage completed. All photos delivered.',
          created_at: '2023-03-10T09:20:00Z'
        },
        {
          id: '5',
          name: 'Colorado State Championship',
          start_date: '2023-10-15',
          end_date: '2023-10-17',
          location: 'Denver, CO',
          venue: 'Ball Arena',
          status: 'negotiation',
          organizer: 'State Athletic Association',
          contact_name: 'Jessica Williams',
          contact_email: 'jwilliams@tournament.com',
          contact_phone: '(970) 555-9876',
          teams_count: 32,
          photography_package: 'Custom Championship Package',
          notes: 'Still finalizing details for exclusive photography rights',
          created_at: '2023-03-20T14:45:00Z'
        }
      ];
      
      setEvents(mockEvents);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setLoading(false);
    }
  };

  const handleManageEvent = (eventId) => {
    // In a full implementation, this would navigate to a detailed event view
    alert(`Navigate to event management for ID: ${eventId}`);
  };

  // Filter events by status and search term
  const filteredEvents = events.filter(event => {
    const matchesSearch = 
      event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.organizer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.contact_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || event.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Helper function for status labels
  const getStatusLabel = (status) => {
    const labels = {
      'upcoming': 'Upcoming',
      'active': 'Active',
      'completed': 'Completed',
      'negotiation': 'In Negotiation',
      'canceled': 'Canceled'
    };
    
    return labels[status] || status;
  };

  return (
    <div className="event-relations-container">
      <div className="section-header">
        <h3>Event Relationships</h3>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchEvents}
            title="Refresh Events"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button 
            className="add-button"
            onClick={() => alert('Navigate to event creation form')}
          >
            <i className="fas fa-calendar-plus"></i> Add Event
          </button>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="events-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="upcoming">Upcoming</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="negotiation">In Negotiation</option>
            <option value="canceled">Canceled</option>
          </select>
        </div>
      </div>
      
      {/* Events List */}
      {loading && events.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading events...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-calendar-alt"></i>
          </div>
          <h3>No Events Found</h3>
          <p>{searchTerm || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'Add an event to get started'}</p>
          {!searchTerm && filterStatus === 'all' && (
            <button 
              className="add-button"
              onClick={() => alert('Navigate to event creation form')}
            >
              <i className="fas fa-calendar-plus"></i> Add Event
            </button>
          )}
        </div>
      ) : (
        <div className="events-card-grid">
          {filteredEvents.map(event => (
            <div className="event-card" key={event.id}>
              <div className="event-card-header">
                <span className={`status-badge ${event.status}`}>
                  {getStatusLabel(event.status)}
                </span>
                <div className="event-actions">
                  <button 
                    className="event-action manage"
                    onClick={() => handleManageEvent(event.id)}
                    title="Manage Event"
                  >
                    <i className="fas fa-cog"></i>
                  </button>
                </div>
              </div>
              
              <h3 className="event-name">{event.name}</h3>
              
              <div className="event-dates">
                <i className="fas fa-calendar-alt"></i>
                {formatDate(event.start_date)}
                {event.end_date && event.start_date !== event.end_date && 
                 ` - ${formatDate(event.end_date)}`}
              </div>
              
              <div className="event-details">
                <div className="event-location">
                  <i className="fas fa-map-marker-alt"></i> {event.location}
                </div>
                {event.venue && (
                  <div className="event-venue">
                    <i className="fas fa-building"></i> {event.venue}
                  </div>
                )}
                <div className="event-teams">
                  <i className="fas fa-users"></i> {event.teams_count} Teams
                </div>
                <div className="event-organizer">
                  <i className="fas fa-user-tie"></i> {event.organizer}
                </div>
              </div>
              
              <div className="event-package">
                <h4>Photography Package</h4>
                <p>{event.photography_package || 'No package selected'}</p>
              </div>
              
              <div className="event-contact">
                <h4>Primary Contact</h4>
                {event.contact_name ? (
                  <>
                    <div className="contact-name">{event.contact_name}</div>
                    <div className="contact-methods">
                      {event.contact_email && (
                        <a href={`mailto:${event.contact_email}`} className="contact-method">
                          <i className="fas fa-envelope"></i> Email
                        </a>
                      )}
                      {event.contact_phone && (
                        <a href={`tel:${event.contact_phone}`} className="contact-method">
                          <i className="fas fa-phone"></i> Call
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="no-contact">No contact assigned</div>
                )}
              </div>
              
              {event.notes && (
                <div className="event-notes">
                  <h4>Notes</h4>
                  <p>{event.notes}</p>
                </div>
              )}
              
              <div className="card-footer">
                <button className="text-button">
                  <i className="fas fa-comment-alt"></i> Log Communication
                </button>
                <button className="text-button">
                  <i className="fas fa-clipboard-list"></i> View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventRelations; 